/**
 * SIRGAS 2000 / UTM zona 22S (EPSG:31982) <-> graus (EPSG:4326).
 *
 * A equipe de campo trabalha em UTM; o banco guarda a geometria em graus. A
 * conversão aqui serve para digitar/exibir no navegador sem ida ao servidor.
 * Série de Krüger até n³ (precisão milimétrica dentro da zona). WGS 84 e
 * SIRGAS 2000 diferem em centímetros e são tratados como equivalentes, como o
 * PostGIS faz entre 4326 e 31982.
 */

export const UTM_SRID = 31982;
export const UTM_LABEL = "SIRGAS 2000 / UTM 22S (EPSG:31982)";

const a = 6378137;
const f = 1 / 298.257222101; // GRS80
const k0 = 0.9996;
const E0 = 500000;
const N0 = 10000000; // hemisfério sul
const lon0 = (-51 * Math.PI) / 180; // meridiano central da zona 22

const n = f / (2 - f);
const A = (a / (1 + n)) * (1 + n ** 2 / 4 + n ** 4 / 64);
const alpha = [n / 2 - (2 * n ** 2) / 3 + (5 * n ** 3) / 16, (13 * n ** 2) / 48 - (3 * n ** 3) / 5, (61 * n ** 3) / 240];
const beta = [n / 2 - (2 * n ** 2) / 3 + (37 * n ** 3) / 96, n ** 2 / 48 + n ** 3 / 15, (17 * n ** 3) / 480];
const delta = [2 * n - (2 * n ** 2) / 3 - 2 * n ** 3, (7 * n ** 2) / 3 - (8 * n ** 3) / 5, (56 * n ** 3) / 15];
const c = (2 * Math.sqrt(n)) / (1 + n);

export function toUtm22S(latitude: number, longitude: number): { e: number; n: number } {
  const phi = (latitude * Math.PI) / 180;
  const dl = (longitude * Math.PI) / 180 - lon0;
  const t = Math.sinh(Math.atanh(Math.sin(phi)) - c * Math.atanh(c * Math.sin(phi)));
  const xi = Math.atan(t / Math.cos(dl));
  const eta = Math.atanh(Math.sin(dl) / Math.sqrt(1 + t * t));
  let e = eta;
  let nn = xi;
  for (let j = 1; j <= 3; j++) {
    e += alpha[j - 1] * Math.cos(2 * j * xi) * Math.sinh(2 * j * eta);
    nn += alpha[j - 1] * Math.sin(2 * j * xi) * Math.cosh(2 * j * eta);
  }
  return { e: E0 + k0 * A * e, n: N0 + k0 * A * nn };
}

export function fromUtm22S(easting: number, northing: number): { latitude: number; longitude: number } {
  const xi = (northing - N0) / (k0 * A);
  const eta = (easting - E0) / (k0 * A);
  let xi1 = xi;
  let eta1 = eta;
  for (let j = 1; j <= 3; j++) {
    xi1 -= beta[j - 1] * Math.sin(2 * j * xi) * Math.cosh(2 * j * eta);
    eta1 -= beta[j - 1] * Math.cos(2 * j * xi) * Math.sinh(2 * j * eta);
  }
  const chi = Math.asin(Math.sin(xi1) / Math.cosh(eta1));
  let phi = chi;
  for (let j = 1; j <= 3; j++) phi += delta[j - 1] * Math.sin(2 * j * chi);
  const lambda = lon0 + Math.atan(Math.sinh(eta1) / Math.cos(xi1));
  return { latitude: (phi * 180) / Math.PI, longitude: (lambda * 180) / Math.PI };
}

/** Faixa plausível da zona 22S no Brasil central; fora disso, provável troca E/N. */
export function isPlausibleUtm22S(easting: number, northing: number): boolean {
  return easting > 160000 && easting < 840000 && northing > 6500000 && northing < 10000000;
}

export function formatUtm(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
